import type { RoadStageSpec, Track } from './types';
import type { RoadDamage } from './damage';
import { Noise2D } from './noise';

/**
 * Corridor raster: for every 1 m cell within CORRIDOR metres of the centreline, the index of the
 * nearest track sample (tiles of 32 × 32 cells, allocated only near the road). O(1) lookups for
 * surfaces, terrain shaping and vegetation exclusion.
 */
export const CORRIDOR = 46;
const TILE = 32;
const EMPTY = 0xffff;

export class CorridorRaster {
  private tiles = new Map<number, Uint16Array>();
  private best = new Map<number, Float32Array>();

  private key(tx: number, tz: number): number {
    return (tx + 4096) * 8192 + (tz + 4096);
  }

  private tile(tx: number, tz: number, create: boolean): Uint16Array | undefined {
    const k = this.key(tx, tz);
    let t = this.tiles.get(k);
    if (!t && create) {
      t = new Uint16Array(TILE * TILE).fill(EMPTY);
      this.tiles.set(k, t);
      this.best.set(k, new Float32Array(TILE * TILE).fill(1e9));
    }
    return t;
  }

  constructor(track: Track) {
    // Stamp cross-sections from every sample; more along-offsets in tight corners so the outside
    // of hairpins has no holes.
    const n = track.s.length;
    for (let i = 0; i < n; i++) {
      const tx = track.tx[i];
      const tz = track.tz[i];
      const nx = tz;
      const nz = -tx;
      const k = Math.abs(track.curvature[i]);
      const alongR = 0.6 * (1 + CORRIDOR * k);
      for (let a = -alongR; a <= alongR; a += 0.5) {
        for (let d = -CORRIDOR; d <= CORRIDOR; d += 0.5) {
          const x = track.x[i] + tx * a + nx * d;
          const z = track.z[i] + tz * a + nz * d;
          const cx = Math.floor(x);
          const cz = Math.floor(z);
          const tix = Math.floor(cx / TILE);
          const tiz = Math.floor(cz / TILE);
          const t = this.tile(tix, tiz, true)!;
          const b = this.best.get(this.key(tix, tiz))!;
          const ci = (cz - tiz * TILE) * TILE + (cx - tix * TILE);
          const dist = a * a + d * d;
          if (dist < b[ci]) {
            b[ci] = dist;
            t[ci] = i;
          }
        }
      }
    }
    this.best.clear();
  }

  /** Nearest sample index for a world point, or −1 outside the corridor. */
  at(x: number, z: number): number {
    const cx = Math.floor(x);
    const cz = Math.floor(z);
    const tix = Math.floor(cx / TILE);
    const tiz = Math.floor(cz / TILE);
    const t = this.tile(tix, tiz, false);
    if (!t) return -1;
    const v = t[(cz - tiz * TILE) * TILE + (cx - tix * TILE)];
    return v === EMPTY ? -1 : v;
  }

  get tileCount(): number {
    return this.tiles.size;
  }
}

/** Smooth "membrane" interpolating road elevation across the map (coarse grid). */
export class BaseElevation {
  readonly x0: number;
  readonly z0: number;
  readonly cell = 16;
  readonly nx: number;
  readonly nz: number;
  readonly h: Float32Array;
  /** Distance (m) from each coarse cell to the road, by BFS. */
  readonly dist: Float32Array;
  /** Nearest sample index per coarse cell. */
  readonly near: Int32Array;

  constructor(track: Track, bounds: { minX: number; maxX: number; minZ: number; maxZ: number }) {
    this.x0 = bounds.minX;
    this.z0 = bounds.minZ;
    this.nx = Math.ceil((bounds.maxX - bounds.minX) / this.cell) + 1;
    this.nz = Math.ceil((bounds.maxZ - bounds.minZ) / this.cell) + 1;
    const N = this.nx * this.nz;
    this.h = new Float32Array(N);
    this.dist = new Float32Array(N).fill(1e9);
    this.near = new Int32Array(N).fill(-1);
    const fixed = new Uint8Array(N);
    const sum = new Float32Array(N);
    const cnt = new Float32Array(N);
    for (let i = 0; i < track.s.length; i++) {
      const cx = Math.round((track.x[i] - this.x0) / this.cell);
      const cz = Math.round((track.z[i] - this.z0) / this.cell);
      if (cx < 0 || cz < 0 || cx >= this.nx || cz >= this.nz) continue;
      const k = cz * this.nx + cx;
      sum[k] += track.y[i];
      cnt[k]++;
      fixed[k] = 1;
      const d = Math.hypot(track.x[i] - (this.x0 + cx * this.cell), track.z[i] - (this.z0 + cz * this.cell));
      if (d < this.dist[k]) {
        this.dist[k] = d;
        this.near[k] = i;
      }
    }
    let mean = 0;
    let m = 0;
    for (let k = 0; k < N; k++) {
      if (fixed[k]) {
        this.h[k] = sum[k] / cnt[k];
        mean += this.h[k];
        m++;
      }
    }
    mean /= Math.max(1, m);
    for (let k = 0; k < N; k++) if (!fixed[k]) this.h[k] = mean;
    // Distance transform (two-pass chamfer) seeded from road cells.
    const D = this.dist;
    const NR = this.near;
    const c1 = this.cell;
    const c2 = this.cell * Math.SQRT2;
    for (let pass = 0; pass < 2; pass++) {
      for (let zz = 0; zz < this.nz; zz++) {
        const z = pass === 0 ? zz : this.nz - 1 - zz;
        for (let xx = 0; xx < this.nx; xx++) {
          const x = pass === 0 ? xx : this.nx - 1 - xx;
          const k = z * this.nx + x;
          const dir = pass === 0 ? -1 : 1;
          const nb: [number, number, number][] = [
            [x + dir, z, c1],
            [x, z + dir, c1],
            [x + dir, z + dir, c2],
            [x - dir, z + dir, c2],
          ];
          for (const [ax, az, w] of nb) {
            if (ax < 0 || az < 0 || ax >= this.nx || az >= this.nz) continue;
            const kk = az * this.nx + ax;
            if (D[kk] + w < D[k]) {
              D[k] = D[kk] + w;
              NR[k] = NR[kk];
            }
          }
        }
      }
    }
    // Seed free cells with the nearest road elevation, then relax (Gauss-Seidel) to a smooth membrane.
    for (let k = 0; k < N; k++) if (!fixed[k] && this.near[k] >= 0) this.h[k] = track.y[this.near[k]];
    for (let it = 0; it < 70; it++) {
      for (let z = 0; z < this.nz; z++) {
        for (let x = 0; x < this.nx; x++) {
          const k = z * this.nx + x;
          if (fixed[k]) continue;
          let s = 0;
          let c = 0;
          if (x > 0) {
            s += this.h[k - 1];
            c++;
          }
          if (x < this.nx - 1) {
            s += this.h[k + 1];
            c++;
          }
          if (z > 0) {
            s += this.h[k - this.nx];
            c++;
          }
          if (z < this.nz - 1) {
            s += this.h[k + this.nx];
            c++;
          }
          this.h[k] = s / c;
        }
      }
    }
  }

  private bilerp(arr: Float32Array, x: number, z: number): number {
    const fx = Math.max(0, Math.min(this.nx - 1.001, (x - this.x0) / this.cell));
    const fz = Math.max(0, Math.min(this.nz - 1.001, (z - this.z0) / this.cell));
    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const u = fx - ix;
    const v = fz - iz;
    const k = iz * this.nx + ix;
    const a = arr[k] + (arr[k + 1] - arr[k]) * u;
    const b = arr[k + this.nx] + (arr[k + this.nx + 1] - arr[k + this.nx]) * u;
    return a + (b - a) * v;
  }

  heightAt(x: number, z: number): number {
    return this.bilerp(this.h, x, z);
  }

  distanceAt(x: number, z: number): number {
    return this.bilerp(this.dist, x, z);
  }

  nearestSample(x: number, z: number): number {
    const cx = Math.max(0, Math.min(this.nx - 1, Math.round((x - this.x0) / this.cell)));
    const cz = Math.max(0, Math.min(this.nz - 1, Math.round((z - this.z0) / this.cell)));
    return this.near[cz * this.nx + cx];
  }
}

export interface Stream {
  /** Point where the stream crosses the road and its direction (unit) across the road. */
  x: number;
  z: number;
  dx: number;
  dz: number;
  depth: number;
  width: number;
  /** Water surface elevation. */
  waterY: number;
}

/**
 * Terrain height model shared by meshes, colliders, vegetation and cameras:
 *  - on the road: centreline elevation + camber + ruts + small surface bumps;
 *  - shoulders slope into ditches, banks blend into natural terrain;
 *  - natural terrain = road-following membrane + fBm hills whose amplitude grows away from the road;
 *  - stream channels carved across the road at the ford and the bridge.
 */
export class TerrainModel {
  readonly track: Track;
  readonly raster: CorridorRaster;
  readonly base: BaseElevation;
  readonly streams: Stream[] = [];
  private readonly noise: Noise2D;
  private readonly bump: Noise2D;
  private readonly amp: number;
  /** Per-sample ditch depth (0 on tarmac/hamlet and bridges). */
  readonly ditch: Float32Array;
  /** Per-sample rut depth. */
  readonly rut: Float32Array;
  /** Samples on the bridge deck (no terrain support, no ditches). */
  readonly bridge: Uint8Array;

  /** Potholes, washouts and rut sections (applied in roadProfile). */
  readonly damage: RoadDamage | null;

  constructor(spec: RoadStageSpec, track: Track, bounds: { minX: number; maxX: number; minZ: number; maxZ: number }, damage: RoadDamage | null = null) {
    this.damage = damage;
    this.track = track;
    this.raster = new CorridorRaster(track);
    this.base = new BaseElevation(track, bounds);
    this.noise = new Noise2D(spec.seed);
    this.bump = new Noise2D(spec.seed ^ 0x5bd1e995);
    this.amp = spec.terrain.amplitude;
    const n = track.s.length;
    this.ditch = new Float32Array(n).fill(0.55);
    this.rut = new Float32Array(n);
    this.bridge = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      const sid = track.surface[i];
      // surface indices: see SURFACE_IDS order in sim/surfaces.ts
      this.rut[i] = sid === 0 || sid === 1 ? 0 : sid === 4 ? 0.07 : 0.035;
      if (sid === 0) this.ditch[i] = 0.15;
      const deep = damage?.rutDepth(track.s[i]) ?? -1;
      if (deep >= 0) this.rut[i] = Math.max(this.rut[i], deep);
    }
    for (const z of spec.zones ?? []) {
      if (z.type === 'hamlet') for (let i = Math.floor(z.from); i <= Math.min(n - 1, Math.ceil(z.to)); i++) this.ditch[i] = 0.1;
    }
    for (const p of spec.props) {
      if (p.type !== 'bridge' || p.to === undefined) continue;
      const a = Math.floor(p.s);
      const b = Math.ceil(p.to);
      for (let i = a; i <= b && i < n; i++) {
        this.bridge[i] = 1;
        this.ditch[i] = 0;
        this.rut[i] = 0;
      }
      const mid = Math.round((a + b) / 2);
      this.streams.push({ x: track.x[mid], z: track.z[mid], dx: track.tx[mid], dz: track.tz[mid], depth: 3.4, width: (b - a) * 0.8, waterY: track.y[mid] - 2.6 });
    }
    for (const p of spec.patches) {
      if (p.surface !== 'water') continue;
      const mid = Math.round((p.from + p.to) / 2);
      if (mid >= n) continue;
      this.streams.push({ x: track.x[mid], z: track.z[mid], dx: track.tx[mid], dz: track.tz[mid], depth: 0.7, width: p.to - p.from, waterY: track.y[mid] + 0.12 });
    }
  }

  /** Natural terrain (no road shaping). */
  natural(x: number, z: number, roadDist: number): number {
    let h = this.base.heightAt(x, z);
    const w = smoothstep(18, 160, roadDist);
    if (w > 0) h += this.noise.fbm(x / 420, z / 420, 4) * this.amp * w;
    const wd = smoothstep(8, 60, roadDist);
    if (wd > 0) h += this.noise.fbm(x / 37, z / 37, 2) * 2.2 * wd;
    // Far ridges for the skyline.
    const wf = smoothstep(350, 900, roadDist);
    if (wf > 0) h += wf * (60 + this.noise.fbm(x / 900 + 3.1, z / 900 - 1.7, 3) * 90);
    if (this.streams.length) h += this.streamCut(x, z, roadDist);
    return h;
  }

  private streamCut(x: number, z: number, roadDist: number): number {
    let cut = 0;
    for (const st of this.streams) {
      // Distance from the stream's centreline (a line through (x, z) along the road tangent's normal).
      const rx = x - st.x;
      const rz = z - st.z;
      const along = rx * st.dx + rz * st.dz; // offset along the road direction = across the stream
      const acrossStream = Math.abs(along);
      const lenAlong = Math.abs(rx * st.dz - rz * st.dx);
      if (acrossStream > st.width * 1.6 || lenAlong > 400) continue;
      const prof = 1 - smoothstep(st.width * 0.35, st.width * 1.6, acrossStream);
      cut = Math.min(cut, -st.depth * prof);
    }
    void roadDist;
    return cut;
  }

  /** Road surface height at sample i and lateral offset d (+ left), without terrain blending. */
  roadProfile(i: number, d: number, x: number, z: number, withDamage = true): number {
    const t = this.track;
    const hw = t.width[i] / 2;
    const ad = Math.abs(d);
    let y = t.y[i] + t.camber[i] * Math.max(-hw, Math.min(hw, d));
    // Small surface undulations (stronger on gravel than tarmac).
    const sid = t.surface[i];
    const rough = sid === 0 || sid === 1 ? 0.012 : 0.035;
    y += this.bump.noise(x / 6.5, z / 6.5) * rough * 2 + this.bump.noise(x / 2.1 + 7, z / 2.1) * rough * 0.6;
    // Ruts in the wheel tracks (±0.8 m from the line).
    const rd = this.rut[i];
    if (rd > 0) {
      const r = Math.abs(ad - 0.82);
      if (r < 0.35) y -= rd * 0.5 * (1 + Math.cos((Math.PI * r) / 0.35));
    }
    // Potholes and washouts (on the carriageway and its loose edge).
    if (withDamage && this.damage && ad <= hw + 1) y -= this.damage.depthAt(i, d, this.damage.along(i, x, z));
    if (ad <= hw) return y;
    // Shoulder and ditch.
    const edge = t.y[i] + t.camber[i] * Math.sign(d) * hw;
    const o = ad - hw;
    const dd = this.ditch[i];
    if (o < 1.0) return edge - o * 0.12;
    if (o < 3.2) return edge - 0.12 - dd * 0.5 * (1 - Math.cos((Math.PI * (o - 1.0)) / 1.1)) * (o < 2.1 ? 1 : 1);
    return edge - 0.12;
  }

  /** Full terrain height at (x, z). */
  heightAt(x: number, z: number): number {
    const i = this.raster.at(x, z);
    if (i < 0) return this.natural(x, z, Math.max(CORRIDOR, this.base.distanceAt(x, z)));
    const t = this.track;
    const dx = x - t.x[i];
    const dz = z - t.z[i];
    const d = dx * t.tz[i] - dz * t.tx[i];
    const ad = Math.abs(d);
    const hw = t.width[i] / 2;
    const nat = this.natural(x, z, ad);
    if (this.bridge[i] && ad > hw - 0.5) return nat; // under/around the bridge deck: stream channel
    const prof = this.roadProfile(i, d, x, z);
    const blend = smoothstep(hw + 3.2, hw + 28, ad);
    return prof + (nat - prof) * blend;
  }

  /** Signed lateral offset and sample index of (x, z) (index −1 outside the corridor). */
  lateral(x: number, z: number): { i: number; d: number } {
    const i = this.raster.at(x, z);
    if (i < 0) return { i: -1, d: Infinity };
    const t = this.track;
    return { i, d: (x - t.x[i]) * t.tz[i] - (z - t.z[i]) * t.tx[i] };
  }
}

export function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
